import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { H1, H2, Body } from '../components/ui/Typography';
import ArticleStructuredData from '../components/common/ArticleStructuredData';

export default function PressRelease4() {
  const title = "RentFi Announces First Property Acquisition Strategy in the Colombian Market";
  const description = "RentFi reveals its strategic entry into the Colombian real estate market with its first targeted property acquisition in Medellín, showcasing its innovative approach to generating passive income through real estate investment.";
  const publishDate = "2024-09-12T00:00:00+00:00";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content="RentFi Colombia, Medellín real estate, property acquisition, passive income, blockchain real estate, SPV structure" />
        <link rel="canonical" href="https://rentfi.io/press/colombian-market-strategy" />
        
        {/* Open Graph Tags */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content="https://rentfi.io/press/colombian-market-strategy" />
        <meta property="og:image" content="https://i.postimg.cc/hPNFtcr4/rentfi-logo.png" />
        <meta property="article:published_time" content={publishDate} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@RentFi_io" />
        <meta name="twitter:creator" content="@RentFi_io" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content="https://i.postimg.cc/hPNFtcr4/rentfi-logo.png" />
        
        {/* Article-specific Meta Tags */}
        <meta name="article:section" content="Press Releases" />
        <meta name="article:tag" content="Real Estate Investment, Colombia Market, Property Acquisition, Medellín Real Estate" />
      </Helmet>

      <ArticleStructuredData
        title={title}
        description={description}
        publishDate={publishDate}
      />

      <main className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link
              to="/press"
              className="inline-flex items-center text-primary hover:text-primary-dark mb-8 group"
              aria-label="Return to press releases list"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Press Releases
            </Link>

            <article className="bg-white rounded-xl shadow-lg p-8">
              <header className="mb-8">
                <time dateTime="2024-09-12" className="text-gray-500 mb-4 block">September 12, 2024</time>
                <H1 className="text-primary mb-4">RentFi Announces First Property Acquisition Strategy in the Colombian Market</H1>
                <address className="text-gray-500 italic mb-8 not-italic">ROAD TOWN, BVI</address>
              </header>

              <div className="prose max-w-none">
                <Body className="text-gray-600 mb-6">
                  RentFi, the innovative real estate investment platform built on blockchain technology, today revealed its strategic entry into the Colombian real estate market, marking a significant milestone in delivering ways of generating income for global investors. The announcement details the platform's first targeted property acquisition in Medellín, showcasing how earning a passive income through real estate will be accessible to everyone.
                </Body>

                <Body className="text-gray-600 mb-6">
                  "Understanding passive income opportunities in emerging markets is crucial for modern investors," stated the RentFi Foundation. "We're excited to announce our first property target in Medellín, Colombia, a city that exemplifies the kinds of high-yield properties we seek for our portfolio."
                </Body>

                <H2 className="text-primary mb-4">Innovative Property Management Approach</H2>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li className="text-gray-600">Establishment of a dedicated Colombian SPV (Special Purpose Vehicle) structure</li>
                  <li className="text-gray-600">Partnership with local property management experts</li>
                  <li className="text-gray-600">Implementation of transparent yield distribution mechanisms</li>
                  <li className="text-gray-600">Development of completely passive income streams for token holders</li>
                </ul>

                <Body className="text-gray-600 mb-6">
                  "Getting passive income from international real estate traditionally requires significant capital and local knowledge," explained the Foundation. "Our SPV structure eliminates these barriers, creating different passive incomes streams that are accessible to anyone holding RENT tokens."
                </Body>

                <H2 className="text-primary mb-4">Medellín Property Strategy</H2>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li className="text-gray-600">Focus on premium locations with strong tourism growth</li>
                  <li className="text-gray-600">Properties featuring luxury amenities and unique characteristics</li>
                  <li className="text-gray-600">Professional management partnerships for optimal yields</li>
                  <li className="text-gray-600">Strategic positioning for long-term appreciation</li>
                </ul>

                <Body className="text-gray-600 mb-6">
                  "The truth about passive income in real estate is that it requires robust infrastructure," the Foundation added. "Our SPV model ensures proper legal compliance while maximizing returns through efficient property management and cost control."
                </Body>

                <H2 className="text-primary mb-4">SPV Structure Benefits</H2>
                <ul className="list-disc pl-6 mb-6 space-y-2">
                  <li className="text-gray-600">Handle local property ownership and management</li>
                  <li className="text-gray-600">Process rental income distribution</li>
                  <li className="text-gray-600">Manage tax compliance and reporting</li>
                  <li className="text-gray-600">Ensure efficient fund flow to token holders</li>
                </ul>

                <Body className="text-gray-600 mb-6">
                  This announcement follows months of careful market analysis and legal structuring, demonstrating RentFi's commitment to creating sustainable weekly passive income opportunities for investors globally.
                </Body>

                <Body className="text-gray-600 mb-6">
                  For more information about RentFi's property acquisition strategy and SPV model, visit{' '}
                  <a 
                    href="https://rentfi.io" 
                    className="text-secondary hover:text-secondary-dark"
                    aria-label="Visit RentFi's official website for more information about property acquisition strategy"
                  >
                    rentfi.io
                  </a>
                </Body>

                <H2 className="text-primary mb-4">About RentFi</H2>
                <Body className="text-gray-600 mb-6">
                  RentFi is developing the first truly accessible real estate investment platform on blockchain, democratizing property investment through innovative tokenization and yield distribution mechanisms.
                </Body>

                <H2 className="text-primary mb-4">Media Contact</H2>
                <Body className="text-gray-600">
                  Block Consulting —{' '}
                  <a 
                    href="https://blockconsulting.cc" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-secondary hover:text-secondary-dark"
                    aria-label="Visit Block Consulting's website"
                  >
                    https://blockconsulting.cc
                  </a>
                </Body>
              </div>
            </article>
          </motion.div>
        </div>
      </main>
    </>
  );
}